import { useEffect, useMemo, useRef, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { Zahlenfeld } from '../ui/Zahlenfeld.js'
import { SignaturePad, type SignaturePadHandle } from '../ui/SignaturePad.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useCacheTable, useSearch } from '../ui/hooks.js'
import { CATALOG_SEARCH } from '../net/search.js'
import {
  attachPhoto,
  createRapport,
  signRapport,
  updateRapport,
  type RapportLineInput
} from '../net/rapports.js'
import { photosFor } from '../net/photoQueue.js'
import type { CacheRow } from '../net/localDb.js'
import { isOnline } from '../net/syncEngine.js'

/**
 * Der Rapportbildschirm.
 *
 * Zielzeit: unter 60 Sekunden, mit Handschuhen, im Keller, ohne Empfang. Das
 * bestimmt jede Entscheidung hier:
 *
 * · Datum steht auf heute. Niemand tippt ein Datum ein.
 * · Stunden kommen über grosse Knöpfe in Viertelstunden, nicht über eine
 *   Tastatur. Feintippen geht trotzdem.
 * · Die Leistungssuche zeigt die zuletzt verwendeten zuerst – auf einer
 *   Baustelle wiederholt sich die Arbeit.
 * · Freitext ist ein Knopf, kein verstecktes Feld: was nicht im Katalog steht,
 *   darf nicht verlorengehen, nur weil das Erfassen umständlich wäre.
 * · Fotos und Unterschrift sind am Ende, weil sie das Längste dauern.
 */

interface Props {
  session: SessionUser
  projectId: string
  rapportId?: string
  onDone: () => void
  onCancel: () => void
}

const VIERTEL = [0.25, 0.5, 1, 2, 4, 8]

function heute(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function RapportForm({
  session,
  projectId,
  rapportId,
  onDone,
  onCancel
}: Props): JSX.Element {
  const { rows: projects } = useCacheTable<CacheRow>('projects')
  const { rows: catalog } = useCacheTable<CacheRow>('catalogItems')

  const [workDate, setWorkDate] = useState(heute())
  const [description, setDescription] = useState('')
  const [isRegie, setIsRegie] = useState(false)
  const [lines, setLines] = useState<RapportLineInput[]>([])
  const [projekt, setProjekt] = useState(projectId)
  const [photos, setPhotos] = useState<Array<{ id: string; status: string }>>([])
  const [signOpen, setSignOpen] = useState(false)
  const [signedByName, setSignedByName] = useState('')
  const [hasInk, setHasInk] = useState(false)
  const [savedId, setSavedId] = useState<string | null>(rapportId ?? null)
  const padRef = useRef<SignaturePadHandle | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const actor = { tenantId: session.tenantId, userId: session.userId }
  const seesPrices = session.role === 'superuser' || session.workerSeesPrices
  const projektName = String(projects.find((p) => p.id === projekt)?.name ?? '—')

  const stunden = lines.filter((l) => l.kind === 'stunden').reduce((s, l) => s + l.quantity, 0)

  useEffect(() => {
    if (!savedId) return
    void photosFor(savedId).then((p) => setPhotos(p.map((x) => ({ id: x.id, status: x.status }))))
  }, [savedId])

  // -------------------------------------------------------------------------

  function addLine(line: RapportLineInput): void {
    setLines((prev) => [...prev, line])
  }

  function removeLine(index: number): void {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  async function speichern(): Promise<string> {
    const eingabe = {
      projectId: projekt,
      workDate,
      description: description.trim(),
      isRegie,
      lines
    }
    if (savedId) {
      // Ohne feste Fassung: updateRapport nimmt den lokalen Stand. Die frühere
      // harte 1 machte jede zweite Änderung zu einem Versionskonflikt.
      await updateRapport(actor, savedId, eingabe)
      return savedId
    }
    const id = await createRapport(actor, eingabe)
    setSavedId(id)
    return id
  }

  return (
    <>
      <div className="card">
        <div className="listhead">
          <h2>Rapport</h2>
          <span className="muted">{projektName}</span>
        </div>

        <div className="row">
          <div className="grow">
            <label htmlFor="r-date">Datum</label>
            <input
              autoComplete="off"
              id="r-date"
              type="date"
              value={workDate}
              max={heute()}
              onChange={(e) => setWorkDate(e.target.value)}
            />
          </div>
        </div>

        <label htmlFor="r-project">Projekt</label>
        <select id="r-project" value={projekt} onChange={(e) => setProjekt(e.target.value)}>
          {projects
            .filter((p) => p.status !== 'archiviert')
            .map((p) => (
              <option key={String(p.id)} value={String(p.id)}>
                {String(p.name)}
              </option>
            ))}
        </select>

        <label htmlFor="r-desc">Was wurde gemacht</label>
        <textarea
          id="r-desc"
          rows={2}
          value={description}
          placeholder="Kurz in eigenen Worten: was wurde ausgeführt?"
          onChange={(e) => setDescription(e.target.value)}
        />

        <label className="inline big">
          <input
            autoComplete="off"
            id="r-regie"
            type="checkbox"
            checked={isRegie}
            onChange={(e) => setIsRegie(e.target.checked)}
          />
          Zusatzarbeit / Regie
        </label>
        <p className="muted small">
          Angehakt heisst: nicht im Auftrag enthalten, wird nach Aufwand verrechnet.
        </p>
      </div>

      <Stunden onAdd={addLine} />

      <Leistungen catalog={catalog} seesPrices={seesPrices} onAdd={addLine} onFreitext={addLine} />

      {lines.length > 0 && (
        <div className="card">
          <div className="listhead">
            <h2>Erfasst</h2>
            <span className="muted">{stunden > 0 ? `${stunden} h` : `${lines.length}`}</span>
          </div>
          <ul className="records">
            {lines.map((l, i) => (
              <li key={`${l.description}-${i}`}>
                <div className="line-row">
                  <span className="rec-name">
                    {l.description}
                    {!l.catalogItemId && <span className="pill small">Freitext</span>}
                    {l.isExtra && <span className="pill small">Regie</span>}
                  </span>
                  <span className="rec-sub">
                    {l.quantity} {l.unit}
                  </span>
                  <button type="button" className="line-remove" onClick={() => removeLine(i)}>
                    Entfernen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h2>Fotos</h2>
        <p className="muted small">
          Werden sofort auf 1600 px verkleinert und einzeln übertragen. Der Rapport wartet nie auf
          ein Foto.
        </p>
        <input
          autoComplete="off"
          id="r-photo"
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0]
            if (!file) return
            try {
              const id = await speichern()
              const info = await attachPhoto(actor, id, file)
              notify.ok(
                `Foto übernommen – aus ${Math.round(info.sourceBytes / 1024)} KB wurden ` +
                  `${Math.round(info.bytes / 1024)} KB${info.rotated ? ', Drehung korrigiert' : ''}.`
              )
              const p = await photosFor(id)
              setPhotos(p.map((x) => ({ id: x.id, status: x.status })))
            } catch (err) {
              notifyError(err)
            } finally {
              if (fileRef.current) fileRef.current.value = ''
            }
          }}
        />
        <ActionButton
          id="btn-foto"
          variant="secondary"
          labels={{ idle: 'Foto aufnehmen', busy: 'Öffnet …' }}
          onAction={async () => fileRef.current?.click()}
        />
        {photos.length > 0 && (
          <p className="muted small">
            {photos.length} Foto{photos.length > 1 ? 's' : ''} lokal ·{' '}
            {photos.filter((p) => p.status === 'confirmed').length} übertragen
          </p>
        )}
      </div>

      <div className="card">
        <div className="actions-row">
          <ActionButton
            id="btn-rapport-speichern"
            labels={{
              idle: savedId ? 'Änderungen sichern' : 'Rapport sichern',
              busy: 'Sichert …',
              success: 'Gesichert'
            }}
            onAction={async () => {
              if (lines.length === 0 && !description.trim()) {
                notify.warn('Der Rapport ist leer.')
                return
              }
              await speichern()
              notify.ok('Gesichert. Geht raus, sobald du Empfang hast.')
              // Zurück zur Übersicht. Ohne das bliebe der Monteur im
              // Formular stehen und wüsste nicht, ob er fertig ist – und die
              // Reiter oben führen ihn hier nicht heraus.
              onDone()
            }}
            onError={(err) => notifyError(err)}
          />
          <ActionButton
            variant="quiet"
            labels={{ idle: 'Abbrechen', busy: '…' }}
            onAction={async () => onCancel()}
          />
        </div>

        <hr className="rule" />

        {!signOpen ? (
          <>
            <ActionButton
              id="btn-unterschreiben"
              variant="secondary"
              labels={{ idle: 'Vom Kunden unterschreiben lassen', busy: 'Öffnet …' }}
              onAction={async () => {
                if (lines.length === 0 && !description.trim()) {
                  notify.warn('Zuerst erfassen, dann unterschreiben.')
                  return
                }
                setSignOpen(true)
              }}
            />
            <p className="muted small">
              Mit der Unterschrift wird der Rapport zum Beleg und lässt sich danach nicht mehr
              ändern. Eine Korrektur geht über einen Nachtrag.
            </p>
          </>
        ) : (
          <>
            <label htmlFor="r-signer">Name der unterschreibenden Person</label>
            <input
              autoComplete="off"
              id="r-signer"
              value={signedByName}
              placeholder="z.B. R. Huber, Hauswart"
              onChange={(e) => setSignedByName(e.target.value)}
            />
            <SignaturePad padRef={padRef} onChange={setHasInk} />

            {!isOnline() && (
              <p className="problem">
                Ohne Empfang lässt sich nicht unterschreiben – die Unterschrift muss vom Server
                bestätigt sein, sonst steht am Ende eine Unterschrift da, die es nicht gibt. Erfasse
                den Rapport jetzt und unterschreibe, sobald du oben wieder Netz hast.
              </p>
            )}

            <div className="actions-row">
              <ActionButton
                id="btn-signieren"
                actionClass="B"
                disabled={!hasInk || !signedByName.trim() || !isOnline()}
                labels={{
                  idle: 'Unterschrift bestätigen',
                  busy: 'Sendet …',
                  explain: 'Server bestätigt …',
                  success: 'Unterschrieben'
                }}
                onAction={async () => {
                  const png = padRef.current?.toPngDataUrl()
                  if (!png) {
                    notify.warn('Es ist noch keine Unterschrift da.')
                    return
                  }
                  const id = await speichern()
                  // Der Rapport muss beim Server sein, bevor er unterschrieben
                  // werden kann – er liegt bis dahin im Ausgangskorb.
                  await new Promise((r) => setTimeout(r, 600))
                  await signRapport(actor, id, {
                    signedByName: signedByName.trim(),
                    signature: png
                  })
                  notify.ok('Unterschrieben. Der Rapport ist jetzt ein Beleg.')
                  onDone()
                }}
                onError={(err) => notifyError(err)}
              />
              <ActionButton
                variant="quiet"
                labels={{ idle: 'Doch nicht', busy: '…' }}
                onAction={async () => setSignOpen(false)}
              />
            </div>
          </>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------

/**
 * Stunden über Knöpfe.
 *
 * Eine Zahlentastatur auf einer Baustelle ist eine Zumutung. Viertelstunden
 * decken ab, was tatsächlich vorkommt; das Feld daneben bleibt für alles
 * andere.
 */
function Stunden({ onAdd }: { onAdd: (line: RapportLineInput) => void }): JSX.Element {
  const [menge, setMenge] = useState(0)
  const [text, setText] = useState('')

  return (
    <div className="card">
      <h2>Stunden</h2>
      <div className="chips">
        {VIERTEL.map((v) => (
          <button
            key={v}
            type="button"
            className={menge === v ? 'chip active' : 'chip'}
            onClick={() => setMenge(v)}
          >
            {v < 1 ? `${v * 60} min` : `${v} h`}
          </button>
        ))}
      </div>
      <div className="row">
        <div className="narrow">
          <label htmlFor="r-h">Stunden</label>
          <Zahlenfeld id="r-h" nachkomma={2} placeholder="0" wert={menge} aufWert={setMenge} />
        </div>
        <div className="grow">
          <label htmlFor="r-h-text">Wofür</label>
          <input
            autoComplete="off"
            id="r-h-text"
            value={text}
            placeholder="Wofür sind die Stunden angefallen?"
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>
      <ActionButton
        id="btn-stunden-hinzu"
        variant="secondary"
        labels={{ idle: 'Stunden hinzufügen', busy: '…' }}
        onAction={async () => {
          if (menge <= 0) {
            notify.warn('Wie viele Stunden?')
            return
          }
          onAdd({
            kind: 'stunden',
            description: text.trim() || 'Arbeitszeit',
            quantity: menge,
            unit: 'Std.',
            isExtra: false
          })
          setMenge(0)
          setText('')
        }}
      />
    </div>
  )
}

/**
 * Katalog und Freitext im selben Block.
 *
 * Absichtlich nebeneinander: Freitext ist die Ausnahme, aber er muss genauso
 * schnell gehen. Was auf der Baustelle nicht erfasst wird, weil es umständlich
 * war, taucht in keiner Rechnung wieder auf.
 */
function Leistungen({
  catalog,
  seesPrices,
  onAdd,
  onFreitext
}: {
  catalog: CacheRow[]
  seesPrices: boolean
  onAdd: (line: RapportLineInput) => void
  onFreitext: (line: RapportLineInput) => void
}): JSX.Element {
  // Deaktivierte Positionen gehören nicht in eine neue Auswahl – der Server
  // würde sie ohnehin abweisen, und eine Liste, aus der man nicht wählen darf,
  // ist eine Falle.
  const waehlbar = useMemo(() => catalog.filter((c) => c.active !== false), [catalog])
  const { query, setQuery, results } = useSearch(waehlbar, CATALOG_SEARCH)
  const [menge, setMenge] = useState('1')
  const [gewaehlt, setGewaehlt] = useState<CacheRow | null>(null)
  const [freitext, setFreitext] = useState('')
  const [freiMenge, setFreiMenge] = useState('1')
  const [freiEinheit, setFreiEinheit] = useState('Stk.')
  const [freiRegie, setFreiRegie] = useState(false)

  return (
    <div className="card">
      <h2>Material und Leistungen</h2>
      <input
        autoComplete="off"
        id="r-katalog-suche"
        type="search"
        placeholder="Leistung suchen …"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setGewaehlt(null)
        }}
      />
      {query && results.length > 0 && (
        <ul className="records compact">
          {results.slice(0, 6).map((c) => (
            <li key={String(c.id)}>
              <button
                type="button"
                className={gewaehlt?.id === c.id ? 'picked' : ''}
                onClick={() => setGewaehlt(c)}
              >
                <span className="rec-name">
                  {c.code ? <span className="code">{String(c.code)}</span> : null}
                  {String(c.name)}
                </span>
                <span className="rec-sub">
                  {String(c.unit)}
                  {seesPrices && c.unitPriceRp != null
                    ? ` · CHF ${formatChf(Number(c.unitPriceRp))}`
                    : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {gewaehlt && (
        <div className="row">
          <div className="narrow">
            <label htmlFor="r-menge">Menge</label>
            <input
              autoComplete="off"
              id="r-menge"
              inputMode="decimal"
              value={menge}
              onChange={(e) => setMenge(e.target.value)}
            />
          </div>
          <div className="grow">
            <label>&nbsp;</label>
            <ActionButton
              id="btn-katalog-hinzu"
              variant="secondary"
              labels={{ idle: `„${String(gewaehlt.name)}“ hinzufügen`, busy: '…' }}
              onAction={async () => {
                const q = Number(menge.replace(',', '.'))
                if (!q || q <= 0) {
                  notify.warn('Welche Menge?')
                  return
                }
                onAdd({
                  kind: String(gewaehlt.kind) === 'material' ? 'material' : 'stunden',
                  catalogItemId: String(gewaehlt.id),
                  description: String(gewaehlt.name),
                  quantity: q,
                  unit: String(gewaehlt.unit),
                  isExtra: false
                })
                setGewaehlt(null)
                setQuery('')
                setMenge('1')
              }}
            />
          </div>
        </div>
      )}

      <hr className="rule" />

      <h3>Nicht im Katalog</h3>
      <p className="muted small">
        Freitext wird erfasst wie jede andere Zeile – er wird aber keine Katalogposition. Ob daraus
        eine werden soll, entscheidet das Büro.
      </p>
      <label htmlFor="r-frei">Was</label>
      <input
        autoComplete="off"
        id="r-frei"
        value={freitext}
        placeholder="z.B. Spezialprofil Alu, 2 m"
        onChange={(e) => setFreitext(e.target.value)}
      />
      <div className="row">
        <div className="narrow">
          <label htmlFor="r-frei-menge">Menge</label>
          <input
            autoComplete="off"
            id="r-frei-menge"
            inputMode="decimal"
            value={freiMenge}
            onChange={(e) => setFreiMenge(e.target.value)}
          />
        </div>
        <div className="narrow">
          <label htmlFor="r-frei-einheit">Einheit</label>
          <input
            autoComplete="off"
            id="r-frei-einheit"
            value={freiEinheit}
            onChange={(e) => setFreiEinheit(e.target.value)}
          />
        </div>
        <div className="grow">
          <label className="inline" htmlFor="r-frei-regie">
            <input
              autoComplete="off"
              id="r-frei-regie"
              type="checkbox"
              checked={freiRegie}
              onChange={(e) => setFreiRegie(e.target.checked)}
            />
            Regie
          </label>
        </div>
      </div>
      <ActionButton
        id="btn-freitext-hinzu"
        variant="secondary"
        labels={{ idle: 'Als Freitext hinzufügen', busy: '…' }}
        onAction={async () => {
          const q = Number(freiMenge.replace(',', '.'))
          if (!freitext.trim()) {
            notify.warn('Was soll erfasst werden?')
            return
          }
          if (!q || q <= 0) {
            notify.warn('Welche Menge?')
            return
          }
          onFreitext({
            kind: 'material',
            catalogItemId: null,
            description: freitext.trim(),
            quantity: q,
            unit: freiEinheit.trim() || 'Stk.',
            isExtra: freiRegie
          })
          setFreitext('')
          setFreiMenge('1')
          setFreiRegie(false)
        }}
      />
    </div>
  )
}
